import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { TOptions } from 'i18next';
import ja from './locales/ja/ui.json';
import en from './locales/en/ui.json';
import { initialUiLanguage, uiLanguageFromI18n, type UiLanguage } from './uiLanguage';

export { UI_LANG_STORAGE_KEY, type UiLanguage } from './uiLanguage';

const jaCatalog = ja as Record<string, string>;
const enCatalog = en as Record<string, string>;

const legacyJaToEn = new Map<string, string>();
for (const [enKey, jaValue] of Object.entries(jaCatalog)) {
  if (typeof jaValue === 'string' && jaValue !== enKey && !legacyJaToEn.has(jaValue)) {
    legacyJaToEn.set(jaValue, enKey);
  }
}

export function resolveUiKey(key: string): string {
  if (
    Object.prototype.hasOwnProperty.call(jaCatalog, key) ||
    Object.prototype.hasOwnProperty.call(enCatalog, key)
  ) {
    return key;
  }
  return legacyJaToEn.get(key) ?? key;
}

const initialLanguage = initialUiLanguage();
if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLanguage;
}

void i18n.use(initReactI18next).init({
  lng: initialLanguage,
  fallbackLng: 'en',
  initImmediate: false,
  resources: {
    ja: { ui: ja },
    en: { ui: en },
  },
  defaultNS: 'ui',
  ns: ['ui'],
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  parseMissingKeyHandler: (key) => key,
  react: { useSuspense: false },
});

export function t(key: string, options?: TOptions): string {
  return i18n.t(resolveUiKey(key), options);
}

/** Japanese catalog value, regardless of the active UI language. */
export function jaText(key: string, options?: TOptions): string {
  return i18n.t(resolveUiKey(key), { ...options, lng: 'ja' });
}

/** English catalog value, regardless of the active UI language. */
export function enText(key: string, options?: TOptions): string {
  return i18n.t(resolveUiKey(key), { ...options, lng: 'en' });
}

/** True when value is the English key or either language's catalog text. */
export function matchesUiText(value: string | null | undefined, key: string): boolean {
  if (value == null) return false;
  const resolved = resolveUiKey(key);
  return value === resolved || value === jaCatalog[resolved] || value === enCatalog[resolved];
}

/** Language the setup agent should reply in. Follows the UI, not the browser. */
export function currentUiLanguage(): UiLanguage {
  return uiLanguageFromI18n(i18n.resolvedLanguage || i18n.language);
}

export default i18n;
